import styled from 'styled-components';
import { useNavigate, useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { Button } from '@/components/ui';
import { errorMessages, getHttpErrorMessage } from '@/utils/errorMessages';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xl};
  background: ${({ theme }) => theme.colors.background};
  direction: rtl;
  text-align: center;
`;

const ErrorIcon = styled.div`
  font-size: 6rem;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  opacity: 0.5;
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-family: ${({ theme }) => theme.fonts.primary};
`;

const Message = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  max-width: 600px;
  font-family: ${({ theme }) => theme.fonts.primary};
  line-height: 1.6;
`;

const ErrorDetails = styled.details`
  margin-top: ${({ theme }) => theme.spacing.lg};
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  max-width: 800px;
  width: 100%;
  text-align: right;
  direction: rtl;

  summary {
    cursor: pointer;
    font-weight: ${({ theme }) => theme.fontWeights.semibold};
    color: ${({ theme }) => theme.colors.textSecondary};
    margin-bottom: ${({ theme }) => theme.spacing.sm};
    font-family: ${({ theme }) => theme.fonts.primary};
  }

  pre {
    background: ${({ theme }) => theme.colors.background};
    padding: ${({ theme }) => theme.spacing.sm};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    overflow-x: auto;
    font-size: ${({ theme }) => theme.fontSizes.sm};
    text-align: left;
    direction: ltr;
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
  justify-content: center;
`;

const ErrorPage: React.FC = () => {
  const navigate = useNavigate();
  const error = useRouteError();

  let errorMessage: string = errorMessages.genericError;
  let errorDetails: string | undefined;

  if (isRouteErrorResponse(error)) {
    errorMessage = getHttpErrorMessage(error.status);
    errorDetails = error.statusText || error.data?.message;
  } else if (error instanceof Error) {
    errorMessage = error.message;
    errorDetails = error.stack;
  }

  return (
    <Container>
      <ErrorIcon>⚠️</ErrorIcon>
      <Title>אירעה שגיאה</Title>
      <Message>{errorMessage}</Message>
      <Message>
        אנחנו מצטערים על אי הנוחות. אנא נסה שוב או חזור לדף הבית.
      </Message>

      <ButtonGroup>
        <Button onClick={() => window.location.reload()} variant="primary">
          רענן דף
        </Button>
        <Button onClick={() => navigate('/')} variant="secondary">
          חזור לדף הבית
        </Button>
        <Button onClick={() => navigate(-1)} variant="outline">
          חזור לדף הקודם
        </Button>
      </ButtonGroup>

      {process.env.NODE_ENV === 'development' && errorDetails && (
        <ErrorDetails>
          <summary>פרטי שגיאה טכניים (למפתחים)</summary>
          <pre>{errorDetails}</pre>
        </ErrorDetails>
      )}
    </Container>
  );
};

export default ErrorPage;
